import { useState } from "react";
import { Copy, LoaderCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePatientVisits, useVisit, type PrescriptionRow } from "@/lib/queries";
import { fmtDate } from "@emr/shared/india";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  patientId: string;
  currentVisitId: string;
  onCopy: (prescription: PrescriptionRow) => void;
}

export function CopyPreviousRxDialog({ open, onOpenChange, patientId, currentVisitId, onCopy }: Props) {
  const { data, isLoading } = usePatientVisits(patientId);
  const visits = (data?.rows ?? []).filter((v) => v.id !== currentVisitId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const detail = useVisit(selectedId ?? undefined);

  const prescription: PrescriptionRow | null = detail.data?.prescriptions[0] ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-maroon-700 dark:text-amber-500">Copy old prescription</DialogTitle>
        </DialogHeader>
        <div className="grid h-[60vh] grid-cols-2 gap-3">
          <ScrollArea className="rounded-lg border border-cream-200 dark:border-dark-700">
            <div className="space-y-2 p-3">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)
              ) : visits.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted">No previous visits.</div>
              ) : (
                visits.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`w-full rounded-lg border p-2.5 text-left transition-colors ${selectedId === v.id ? "border-maroon-400 bg-cream-50 dark:bg-dark-700" : "border-cream-200 bg-white hover:bg-cream-50/50 dark:border-dark-700 dark:bg-dark-800"}`}
                    onClick={() => setSelectedId(v.id)}
                  >
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-ink dark:text-cream-50">{fmtDate(v.checkInAt)}</span>
                      <span className="text-xs text-muted">{v.doctorName ?? "—"}</span>
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted">{v.diagnosis ?? "No diagnosis recorded"}</div>
                  </button>
                ))
              )}
            </div>
          </ScrollArea>

          <div className="flex flex-col rounded-lg border border-cream-200 dark:border-dark-700">
            <ScrollArea className="flex-1">
              <div className="p-3">
                {!selectedId ? (
                  <div className="p-6 text-center text-sm text-muted">Select a visit on the left to preview its prescription.</div>
                ) : detail.isLoading ? (
                  <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
                ) : !prescription || prescription.medicines.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted">This visit has no prescription.</div>
                ) : (
                  <ul className="space-y-2">
                    {prescription.medicines.map((m) => (
                      <li key={m.id} className="rounded-md bg-cream-50 p-2 text-sm dark:bg-dark-800">
                        <div className="font-medium text-ink dark:text-cream-50">{m.serialNo}. {m.name}</div>
                        <div className="text-xs text-muted">
                          {m.taper
                            ? [`Taper (${m.taper.length} periods)`, m.taper.map((s) => `${s.frequency}×${s.days}d`).join(", ")].join(" · ")
                            : [m.strength, m.frequency, m.duration].filter(Boolean).join(" · ")}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </ScrollArea>
            <div className="border-t border-cream-200 p-3 dark:border-dark-700">
              <Button
                className="w-full bg-maroon-600 hover:bg-maroon-700 text-cream-50"
                disabled={!prescription || prescription.medicines.length === 0 || detail.isLoading}
                onClick={() => {
                  if (prescription) {
                    onCopy(prescription);
                    onOpenChange(false);
                  }
                }}
              >
                {detail.isLoading ? <LoaderCircle size={14} className="mr-1 animate-spin" /> : <Copy size={14} className="mr-1" />}
                Copy prescription to current visit
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
